import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from '@peiyou/scratch-vm';
import WavEncoder from 'wav-encoder';
import {connect} from 'react-redux';

import RecordModalComponent from '../components/record-modal/record-modal.jsx';
import {recordSoundLimitSize, uploadExceedAlert} from '../lib/upload-limit';
import {getOssSign, upload, textCheck, toCdnUrl} from '../lib/services/scratch-ser';
import axios from '../lib/services';
import swal from '../lib/swal';
import {queryParams, getUserinfo} from '../lib/services/query-params';
import {getHashParams} from '../lib/query-string';

import {
    closeSoundRecorder
} from '../reducers/modals';

const { role } = queryParams;
const workStatusQuery = getHashParams('workStatus') || queryParams.workStatus;

const getCheckStatus = result => {
    if (result && result.data && result.data.data && result.data.data.status !== undefined) {
        return result.data.data.status;
    }
    if (result && result.data && result.data.status !== undefined) {
        return result.data.status;
    }
    return null;
};

const pollCheck = async params => {
    const startTime = Date.now();
    const timeout = 300000;
    while (Date.now() - startTime < timeout) {
        const result = await axios.post(`${API_BASE_URL}/core/safeCheck/guardian/check`, params);
        const status = getCheckStatus(result);
        if (status === 3 || status === 4 || status === 5) return result;
        if (status !== 1 && status !== 2) return result;
        await new Promise(resolve => window.setTimeout(resolve, 3000));
    }
    throw new Error('guardian/check timeout');
};

const getOssMd5 = async url => {
    try {
        const res = await fetch(url, {method: 'HEAD'});
        const etag = res.headers.get('ETag');
        return etag ? etag.replace(/['"]/g, '').toLowerCase() : null;
    } catch (error) {
        return null;
    }
};

class RecordModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleRecord',
            'handleStopRecording',
            'handlePlay',
            'handleStopPlaying',
            'handleBack',
            'handleSubmit',
            'handleCancel',
            'handleSetPlayhead',
            'handleSetTrimStart',
            'handleSetTrimEnd'
        ]);

        this.state = {
            samples: null,
            encoding: false,
            levels: null,
            playhead: null,
            playing: false,
            recording: false,
            sampleRate: null,
            trimStart: 0,
            trimEnd: 1
        };
    }
    handleRecord () {
        this.setState({recording: true});
    }
    handleStopRecording (samples, sampleRate, levels, trimStart, trimEnd) {
        if (samples.length > 0) {
            this.setState({samples, sampleRate, levels, trimStart, trimEnd, recording: false});
        }
    }
    handlePlay () {
        this.setState({playing: true});
    }
    handleStopPlaying () {
        this.setState({playing: false, playhead: null});
    }
    handleBack () {
        this.setState({playing: false, samples: null});
    }
    handleSetTrimEnd (trimEnd) {
        this.setState({trimEnd});
    }
    handleSetTrimStart (trimStart) {
        this.setState({trimStart});
    }
    handleSetPlayhead (playhead) {
        this.setState({playhead});
    }
    handleSubmit () {
        this.setState({encoding: true}, () => {
            const sampleCount = this.state.samples.length;
            const startIndex = Math.floor(this.state.trimStart * sampleCount);
            const endIndex = Math.floor(this.state.trimEnd * sampleCount);
            const clippedSamples = this.state.samples.slice(startIndex, endIndex);
            WavEncoder.encode({
                sampleRate: this.state.sampleRate,
                channelData: [clippedSamples]
            }).then(async wavBuffer => {
                const vmSound = {
                    format: '',
                    dataFormat: 'wav',
                    rate: this.state.sampleRate,
                    sampleCount: clippedSamples.length
                };

                // Create an asset from the encoded .wav and get resulting md5
                const storage = this.props.vm.runtime.storage;
                vmSound.asset = storage.createAsset(
                    storage.AssetType.Sound,
                    storage.DataFormat.WAV,
                    new Uint8Array(wavBuffer),
                    null,
                    true // generate md5
                );
                const actualSoundSize = (vmSound.asset.data.byteLength / Math.pow(1024, 2)).toFixed(2);
                if (actualSoundSize >= recordSoundLimitSize) {
                    uploadExceedAlert(recordSoundLimitSize, actualSoundSize);
                    this.props.onClose();
                    return false;
                }
                vmSound.assetId = vmSound.asset.assetId;

                // update vmSound object with md5 property
                vmSound.md5 = `${vmSound.assetId}.${vmSound.dataFormat}`;
                // VM 会在重名时自动改成可用名称；缺 name 会导致 scratch-parser 校验失败无法打开
                vmSound.name = 'recording1';
                const addRecordedSound = () => this.props.vm.addSound(vmSound).then(() => {
                    this.props.onClose();
                    this.props.onNewSound();
                });

                if (!workStatusQuery) {
                    addRecordedSound();
                    return;
                }

                const audioFileName = `recording_${Date.now()}.wav`;
                const audioBlob = new Blob([wavBuffer], {type: 'audio/wav'});
                const uploadFile = new File([audioBlob], audioFileName, {type: 'audio/wav'});
                const audioFormat = '.wav';

                try {
                    const response = await getOssSign({
                        params: [{
                            type: 2,
                            fileName: uploadFile.name
                        }]
                    });
                    const signData = response.data.data[0].sign;
                    const businessId = response.data.data[0].savePath || signData.fileName;
                    const uploadRes = await upload(uploadFile, signData);
                    const {id: authorId, realName: authorName} = getUserinfo() || {};
                    const url = toCdnUrl(signData.dir);
                    const fallbackMd5 = ((businessId || signData.fileName || signData.dir || '').split('?')[0].split('/').pop() || '').split('.')[0];
                    const etagMd5 = uploadRes && uploadRes.headers && uploadRes.headers.etag ?
                        uploadRes.headers.etag.replace(/['"]/g, '').toLowerCase() : null;
                    const md5 = etagMd5 || await getOssMd5(url) || fallbackMd5;

                    const textCheckRes = await textCheck({url: uploadFile.name, module: 6});
                    if (getCheckStatus(textCheckRes) === 4) {
                        await swal.alert('提示', `素材音频${audioFormat ? `(${audioFormat.replace('.', '').toUpperCase()})` : ''}不符合平台的发布规范，请重新上传`);
                        this.setState({encoding: false});
                        return;
                    }

                    const result = await pollCheck({
                        type: 2,
                        url,
                        md5,
                        module: 2,
                        authorId,
                        authorName,
                        role,
                        businessId: businessId || md5
                    });
                    if (getCheckStatus(result) === 4) {
                        await swal.alert('提示', `素材音频${audioFormat ? `(${audioFormat.replace('.', '').toUpperCase()})` : ''}不符合平台的发布规范，请重新上传`);
                        this.setState({encoding: false});
                        return;
                    }
                } catch (error) {
                    console.error('record sound check error:', error);
                    await swal.alert('提示', '音频资源校验失败，请稍后重试');
                    this.setState({encoding: false});
                    return;
                }

                addRecordedSound();
            });
        });
    }
    handleCancel () {
        this.props.onClose();
    }
    render () {
        return (
            <RecordModalComponent
                encoding={this.state.encoding}
                levels={this.state.levels}
                playhead={this.state.playhead}
                playing={this.state.playing}
                recording={this.state.recording}
                sampleRate={this.state.sampleRate}
                samples={this.state.samples}
                trimEnd={this.state.trimEnd}
                trimStart={this.state.trimStart}
                onBack={this.handleBack}
                onCancel={this.handleCancel}
                onPlay={this.handlePlay}
                onRecord={this.handleRecord}
                onSetPlayhead={this.handleSetPlayhead}
                onSetTrimEnd={this.handleSetTrimEnd}
                onSetTrimStart={this.handleSetTrimStart}
                onStopPlaying={this.handleStopPlaying}
                onStopRecording={this.handleStopRecording}
                onSubmit={this.handleSubmit}
            />
        );
    }
}

RecordModal.propTypes = {
    onClose: PropTypes.func,
    onNewSound: PropTypes.func,
    vm: PropTypes.instanceOf(VM)
};

const mapStateToProps = state => ({
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    onClose: () => {
        dispatch(closeSoundRecorder());
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(RecordModal);
